import { Popover } from '@material-ui/core';
import { Body, Button, Heading, Icon } from 'copilot-design-system';
import { FC, useContext } from 'react';

import { CopyToClipboard } from 'src/components/CopyToClipboard';
import { RouteContext } from 'src/context';
import history from 'src/history';
import { useAppSelector } from 'src/hooks/useStore';
import {
  selectAllCompaniesMap,
  selectClientsMap,
} from 'src/store/clients/types';
import DateUtils from 'src/utils/DateUtils';

type EntityType = 'client' | 'company';

const getCrmPath = (entityType: EntityType, entityId: string) =>
  entityType === 'client'
    ? `/clients/users/details/${entityId}`
    : `/companies/${entityId}`;

interface ContextBarAvatarPopoverProps {
  entityType: EntityType;
  entityId: string;
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
}

export const ContextBarAvatarPopover: FC<ContextBarAvatarPopoverProps> = ({
  entityType,
  entityId,
  anchorEl,
  open,
  onClose,
}) => {
  const { pathname } = useContext(RouteContext);
  const companies = useAppSelector(selectAllCompaniesMap);
  const clients = useAppSelector(selectClientsMap);

  const crmPath = getCrmPath(entityType, entityId);

  // Check if we're already on the entity's profile page
  const isOnProfilePage = pathname.startsWith(crmPath);

  const client = entityType === 'client' ? clients.get(entityId) : undefined;
  const company =
    entityType === 'company' ? companies.get(entityId) : undefined;

  let entityName = '';
  let email = '';
  let createdDate = '';

  if (client) {
    entityName =
      `${client.fields.givenName ?? ''} ${client.fields.familyName ?? ''}`.trim();
    email = client.fields.email ?? '';
    ({ createdDate } = client);
  } else if (company) {
    entityName = company.fields.name;
    ({ createdDate } = company);
  }

  const handleOpenProfile = () => {
    history.push(crmPath);
    onClose();
  };

  const formattedDate = createdDate ? DateUtils.formatDate(createdDate) : '';

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      PaperProps={{
        style: {
          transform: 'translateX(6px) translateY(4px)',
        },
      }}
      classes={{
        paper:
          'border border-solid border-secondary-hover shadow-dropdown-menu rounded-lg',
      }}
    >
      <div className="p-3 w-[220px]">
        <Heading size="base" className="text-primary mb-[6px]">
          {entityName}
        </Heading>

        <div className="flex flex-col gap-1">
          {email && (
            <CopyToClipboard>
              {({ copy }) => (
                <button
                  className="w-full group cursor-pointer px-0 flex items-center gap-1.5 border-0 bg-transparent hover:bg-transparent"
                  onClick={() => copy(email)}
                >
                  <Icon
                    icon="Email"
                    className="w-3 h-3 text-secondary shrink-0"
                  />
                  <Body size="xs" className="text-primary truncate">
                    {email}
                  </Body>
                  <Icon
                    icon="Copy"
                    className="w-3 h-3 text-secondary shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                  />
                </button>
              )}
            </CopyToClipboard>
          )}

          {formattedDate && (
            <div className="flex items-center gap-1.5">
              <Icon icon="Calendar" className="w-3 h-3 text-secondary" />
              <Body size="xs" className="text-primary">
                Created {formattedDate}
              </Body>
            </div>
          )}
        </div>

        {!isOnProfilePage && (
          <Button
            className="w-full mt-2"
            onClick={handleOpenProfile}
            label="Open profile"
            variant="secondary"
            size="sm"
          />
        )}
      </div>
    </Popover>
  );
};
